"use client";

import { Mark, markInputRule, markPasteRule } from "@tiptap/core";

export interface VariableOptions {
  variablePattern: RegExp;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variable: {
      setVariable: (attrs: { key: string }) => ReturnType;
      toggleVariable: (attrs: { key: string }) => ReturnType;
      unsetVariable: () => ReturnType;
    };
  }
}

// Matches {{variable_name}} with alphanumerics, underscore and dot
export const DEFAULT_VARIABLE_REGEX = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

export const Variable = Mark.create<VariableOptions>({
  name: "variable",

  group: "inline",
  excludes: "variable",
  inclusive: false, // prevent the variable mark from swallowing characters typed immediately after
  spanning: false, // prevent the mark from spanning - each variable is self-contained

  addOptions() {
    return {
      variablePattern: DEFAULT_VARIABLE_REGEX,
    };
  },

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-variable-key"),
        renderHTML: (attributes) => ({ "data-variable-key": attributes.key }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-variable-key]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const key = (HTMLAttributes as any)["data-variable-key"];
    // Render visually as a chip-like span; we'll strip the span on export in the editor and leave {{key}}
    return [
      "span",
      {
        ...HTMLAttributes,
        class:
          (HTMLAttributes.class || "") +
          " inline-flex items-center px-1 rounded bg-amber-100 text-amber-900 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800 text-[0.8em]",
      },
      `{{${key}}}`,
    ];
  },

  addCommands() {
    return {
      setVariable:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
      toggleVariable:
        (attrs) =>
        ({ commands }) =>
          commands.toggleMark(this.name, attrs),
      unsetVariable:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },

  addInputRules() {
    // Disable automatic variable detection via typing to prevent malformed variables
    // Variables can still be:
    // 1. Inserted via the variable picker (programmatically)
    // 2. Detected when pasting content (via paste rules)
    // This prevents issues like "n{{variable}}" being created when typing after text
    return [];
  },

  addPasteRules() {
    const { variablePattern } = this.options;
    return [
      markPasteRule({
        find: new RegExp(variablePattern.source, "g"),
        type: this.type,
        getAttributes: (match) => ({ key: match[1] }),
      }),
    ];
  },
});

// Helper to strip variable spans back to {{key}} for storage
export function replaceVariableSpansWithBraces(html: string): string {
  if (!html) return html;
  return html.replace(
    /<span[^>]*data-variable-key=\"([^\"]+)\"[^>]*>.*?<\/span>/g,
    (_m, key) => `{{${key}}}`
  );
}
